import type { Booking, BookingType, Expense, ExpenseCategory, Flight, Hotel, ReservationLink, ReservationType } from '../types';

/**
 * A reservation (flight, stay or any other booking) as an expense sees it: what
 * to call it, when it is, and which expense category it most likely belongs to.
 */
export interface ReservationOption {
  link: ReservationLink;
  title: string;
  /** YYYY-MM-DD */
  date: string;
  category: ExpenseCategory;
}

const BOOKING_CATEGORY: Record<BookingType, ExpenseCategory> = {
  TRANSPORT:  'TRANSPORT',
  EVENT:      'ACTIVITIES',
  ACTIVITY:   'ACTIVITIES',
  RESTAURANT: 'FOOD',
  OTHER:      'OTHER',
};

export function flightOption(f: Flight): ReservationOption {
  const route = `${f.departureAirport} → ${f.arrivalAirport}`;
  return {
    link: { type: 'FLIGHT', id: f.id },
    title: f.flightNumber ? `${route} · ${f.flightNumber}` : route,
    date: f.departureTime.slice(0, 10),
    category: 'FLIGHTS',
  };
}

export function hotelOption(h: Hotel): ReservationOption {
  return { link: { type: 'HOTEL', id: h.id }, title: h.name, date: h.checkIn.slice(0, 10), category: 'ACCOMMODATION' };
}

export function bookingOption(b: Booking): ReservationOption {
  return { link: { type: 'BOOKING', id: b.id }, title: b.title, date: b.date.slice(0, 10), category: BOOKING_CATEGORY[b.type] };
}

export interface ReservationGroups {
  FLIGHT: ReservationOption[];
  HOTEL: ReservationOption[];
  BOOKING: ReservationOption[];
}

const byDate = (a: ReservationOption, b: ReservationOption) => a.date.localeCompare(b.date);

export function reservationGroups(flights: Flight[], hotels: Hotel[], bookings: Booking[]): ReservationGroups {
  return {
    FLIGHT:  flights.map(flightOption).sort(byDate),
    HOTEL:   hotels.map(hotelOption).sort(byDate),
    BOOKING: bookings.map(bookingOption).sort(byDate),
  };
}

export function expenseReservation(e: Pick<Expense, 'flightId' | 'hotelId' | 'bookingId'>): ReservationLink | null {
  if (e.flightId)  return { type: 'FLIGHT', id: e.flightId };
  if (e.hotelId)   return { type: 'HOTEL', id: e.hotelId };
  if (e.bookingId) return { type: 'BOOKING', id: e.bookingId };
  return null;
}

export const sameReservation = (a: ReservationLink | null, b: ReservationLink | null) =>
  a?.type === b?.type && a?.id === b?.id;

/** `<select>` values are strings; this is the round trip. */
export const encodeReservation = (l: ReservationLink | null) => (l ? `${l.type}:${l.id}` : '');
export function decodeReservation(value: string): ReservationLink | null {
  const [type, id] = value.split(':');
  return type && id ? { type: type as ReservationType, id } : null;
}

interface PrefillState {
  description: string;
  category: ExpenseCategory;
  date: string;
}

/**
 * What picking a reservation fills in. Only fields the user hasn't set: a typed
 * description, a chosen category or a moved date are theirs, not the ticket's.
 */
export function prefillFromReservation<T extends PrefillState>(
  form: T,
  r: ReservationOption,
  touched: { category: boolean; date: boolean },
): T {
  return {
    ...form,
    description: form.description.trim() ? form.description : r.title,
    category: touched.category ? form.category : r.category,
    date: touched.date ? form.date : r.date,
  };
}
